/**
 * Execution Monitor Page
 * Monitor active route executions and material collection progress
 */

import { useState, useEffect } from 'react';
import { Loader2, AlertCircle, CheckCircle, Clock, MapPin, Package, Truck, RefreshCw, Plus, Calendar } from 'lucide-react';
import { transportApi, type RouteExecution } from '../../services/transport/api';

const executionStatusColors: Record<string, { bg: string; text: string; dot: string }> = {
  SCHEDULED: { bg: 'bg-blue-100', text: 'text-blue-800', dot: 'bg-blue-500' },
  STARTED: { bg: 'bg-orange-100', text: 'text-orange-800', dot: 'bg-orange-500' },
  IN_PROGRESS: { bg: 'bg-orange-100', text: 'text-orange-800', dot: 'bg-orange-500' },
  COMPLETED: { bg: 'bg-green-100', text: 'text-green-800', dot: 'bg-green-500' },
  CANCELLED: { bg: 'bg-red-100', text: 'text-red-800', dot: 'bg-red-500' },
  FAILED: { bg: 'bg-red-100', text: 'text-red-800', dot: 'bg-red-500' }
};

function ExecutionCard({ execution }: { execution: RouteExecution }) {
  // Use statusCode or status (backend may return either)
  const executionStatus = execution.statusCode || execution.status || 'SCHEDULED';
  const statusColor = executionStatusColors[executionStatus];
  const completionPercentage = execution.totalStopsCompleted
    ? Math.round((execution.totalStopsCompleted / (execution.totalStopsCompleted + (execution.totalStopsSkipped || 0))) * 100)
    : 0;

  // Determine if execution is from past, today, or future
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const execDate = new Date(execution.executionDate);
  execDate.setHours(0, 0, 0, 0);

  const isPast = execDate < today;
  const isToday = execDate.getTime() === today.getTime();
  const isFuture = execDate > today;

  // Format date for display
  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' });
  };

  return (
    <div className={`rounded-lg border p-6 hover:shadow-lg transition-shadow ${
      isPast ? 'border-red-300 bg-red-50' :
      isToday ? 'border-green-300 bg-green-50' :
      'border-gray-200 bg-white'
    }`}>
      <div className="mb-4 flex items-start justify-between">
        <div>
          <h3 className="font-semibold text-gray-900">
            {execution.routeName || `Route ${execution.routeCode || execution.routeId}`}
          </h3>
          {execution.routeCode && (
            <p className="text-xs text-gray-500 mb-1">{execution.routeCode}</p>
          )}
          <div className="flex items-center gap-2 flex-wrap">
            <p className="text-sm text-gray-600">Date: {formatDate(execution.executionDate)}</p>
            {isPast && (
              <span className="inline-flex items-center rounded-full bg-red-100 px-2 py-0.5 text-xs font-medium text-red-700">
                Overdue
              </span>
            )}
            {isToday && (
              <span className="inline-flex items-center rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-700">
                Today
              </span>
            )}
            {isFuture && (
              <span className="inline-flex items-center rounded-full bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-700">
                Upcoming
              </span>
            )}
          </div>
        </div>
        <span className={`inline-flex items-center gap-1 rounded-full ${statusColor?.bg || 'bg-gray-100'} ${statusColor?.text || 'text-gray-800'} px-3 py-1 text-xs font-medium`}>
          <span className={`h-2 w-2 rounded-full ${statusColor?.dot || 'bg-gray-500'}`}></span>
          {execution.statusName || executionStatus}
        </span>
      </div>

      <div className="grid gap-3 sm:grid-cols-2 mb-4">
        <div className="flex items-center gap-2 text-sm">
          <Truck className="h-4 w-4 text-gray-600" />
          <span className="text-gray-600">Driver: </span>
          <span className="font-medium text-gray-900">{execution.driverName || 'N/A'}</span>
        </div>
        <div className="flex items-center gap-2 text-sm">
          <Package className="h-4 w-4 text-gray-600" />
          <span className="text-gray-600">Vehicle: </span>
          <span className="font-medium text-gray-900">{execution.vehicleNumber || 'N/A'}</span>
        </div>
        <div className="flex items-center gap-2 text-sm">
          <MapPin className="h-4 w-4 text-gray-600" />
          <span className="text-gray-600">Distance: </span>
          <span className="font-medium text-gray-900">{execution.actualDistanceKm?.toFixed(1) || '-'} km</span>
        </div>
        <div className="flex items-center gap-2 text-sm">
          <Clock className="h-4 w-4 text-gray-600" />
          <span className="text-gray-600">Duration: </span>
          <span className="font-medium text-gray-900">{execution.actualDurationMin || '-'} min</span>
        </div>
      </div>

      {/* Collection Progress */}
      <div className="mb-4 rounded-lg bg-gray-50 p-4">
        <div className="mb-2 flex items-center justify-between">
          <p className="text-sm font-medium text-gray-700">Collection Progress</p>
          <p className="text-sm font-medium text-gray-900">{completionPercentage}%</p>
        </div>
        <div className="h-2 w-full rounded-full bg-gray-200">
          <div
            className="h-2 rounded-full bg-green-500 transition-all"
            style={{ width: `${completionPercentage}%` }}
          ></div>
        </div>
        <div className="mt-3 grid gap-2 text-xs text-gray-600">
          <div className="flex justify-between">
            <span>Completed Stops:</span>
            <span className="font-medium text-gray-900">{execution.totalStopsCompleted || 0}</span>
          </div>
          <div className="flex justify-between">
            <span>Skipped Stops:</span>
            <span className="font-medium text-gray-900">{execution.totalStopsSkipped || 0}</span>
          </div>
          <div className="flex justify-between">
            <span>Total Collected:</span>
            <span className="font-medium text-gray-900">{execution.totalQuantityCollectedKg?.toFixed(1) || '0'} kg</span>
          </div>
        </div>
      </div>

      {/* Timestamps */}
      {(execution.startedAt || execution.completedAt) && (
        <div className="border-t border-gray-200 pt-4 space-y-2 text-xs text-gray-600">
          {execution.startedAt && (
            <div className="flex justify-between">
              <span>Started:</span>
              <span>{new Date(execution.startedAt).toLocaleString()}</span>
            </div>
          )}
          {execution.completedAt && (
            <div className="flex justify-between">
              <span>Completed:</span>
              <span>{new Date(execution.completedAt).toLocaleString()}</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function ExecutionMonitorPage() {
  const [executions, setExecutions] = useState<RouteExecution[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState('');
  const [dateFilter, setDateFilter] = useState<'all' | 'overdue' | 'today' | 'upcoming'>('all');
  const [manufacturingUnitId, setManufacturingUnitId] = useState('');
  const [creatingPast, setCreatingPast] = useState(false);
  const [creatingUpcoming, setCreatingUpcoming] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Calculate summary counts
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const overdueCount = executions.filter(e => {
    const d = new Date(e.executionDate);
    d.setHours(0, 0, 0, 0);
    return d < today;
  }).length;

  const todayCount = executions.filter(e => {
    const d = new Date(e.executionDate);
    d.setHours(0, 0, 0, 0);
    return d.getTime() === today.getTime();
  }).length;

  const upcomingCount = executions.filter(e => {
    const d = new Date(e.executionDate);
    d.setHours(0, 0, 0, 0);
    return d > today;
  }).length;

  useEffect(() => {
    fetchExecutions();
  }, [statusFilter, dateFilter, manufacturingUnitId]);

  const fetchExecutions = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch all scheduled/active executions (past pending, today, and future)
      const allExecutions = await transportApi.getExecutions();

      // Apply client-side filters if set
      let filtered = allExecutions;

      if (statusFilter) {
        filtered = filtered.filter(e => (e.statusCode || e.status) === statusFilter);
      }

      // Apply date filter
      const filterToday = new Date();
      filterToday.setHours(0, 0, 0, 0);

      if (dateFilter === 'overdue') {
        filtered = filtered.filter(e => {
          const d = new Date(e.executionDate);
          d.setHours(0, 0, 0, 0);
          return d < filterToday;
        });
      } else if (dateFilter === 'today') {
        filtered = filtered.filter(e => {
          const d = new Date(e.executionDate);
          d.setHours(0, 0, 0, 0);
          return d.getTime() === filterToday.getTime();
        });
      } else if (dateFilter === 'upcoming') {
        filtered = filtered.filter(e => {
          const d = new Date(e.executionDate);
          d.setHours(0, 0, 0, 0);
          return d > filterToday;
        });
      }

      // Sort by execution date (oldest first to show missed routes prominently)
      filtered.sort((a, b) => {
        const dateA = new Date(a.executionDate).getTime();
        const dateB = new Date(b.executionDate).getTime();
        return dateA - dateB;
      });

      setExecutions(filtered);
    } catch (err) {
      console.error('Failed to fetch executions:', err);
      setError(err instanceof Error ? err.message : 'Failed to load executions');
    } finally {
      setLoading(false);
    }
  };

  const handleCreatePastExecutions = async () => {
    try {
      setCreatingPast(true);
      setError(null);
      setSuccessMessage(null);
      const result = await transportApi.createPastExecutions(7);
      setSuccessMessage(`Created ${result.executionsCreated} executions for past 7 days`);
      // Refresh the list
      fetchExecutions();
    } catch (err) {
      console.error('Failed to create past executions:', err);
      setError(err instanceof Error ? err.message : 'Failed to create past executions');
    } finally {
      setCreatingPast(false);
    }
  };

  const handleCreateUpcomingExecutions = async () => {
    try {
      setCreatingUpcoming(true);
      setError(null);
      setSuccessMessage(null);
      const result = await transportApi.createUpcomingExecutions(7);
      setSuccessMessage(`Created ${result.executionsCreated} executions for next 7 days`);
      // Refresh the list
      fetchExecutions();
    } catch (err) {
      console.error('Failed to create upcoming executions:', err);
      setError(err instanceof Error ? err.message : 'Failed to create upcoming executions');
    } finally {
      setCreatingUpcoming(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-gray-900">Execution Monitor</h2>
        <div className="flex gap-2">
          <button
            onClick={handleCreatePastExecutions}
            disabled={creatingPast}
            className="inline-flex items-center gap-2 rounded-lg border border-orange-300 bg-orange-50 px-4 py-2 text-sm font-medium text-orange-700 hover:bg-orange-100 disabled:opacity-50"
          >
            {creatingPast ? <Loader2 className="h-4 w-4 animate-spin" /> : <Calendar className="h-4 w-4" />}
            Backfill Past (7 days)
          </button>
          <button
            onClick={handleCreateUpcomingExecutions}
            disabled={creatingUpcoming}
            className="inline-flex items-center gap-2 rounded-lg border border-blue-300 bg-blue-50 px-4 py-2 text-sm font-medium text-blue-700 hover:bg-blue-100 disabled:opacity-50"
          >
            {creatingUpcoming ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
            Create Upcoming (7 days)
          </button>
          <button
            onClick={() => fetchExecutions()}
            disabled={loading}
            className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </div>

      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4">
          <div className="flex gap-3">
            <AlertCircle className="h-5 w-5 text-red-600" />
            <p className="text-sm text-red-800">{error}</p>
          </div>
        </div>
      )}

      {successMessage && (
        <div className="rounded-lg border border-green-200 bg-green-50 p-4">
          <div className="flex gap-3">
            <CheckCircle className="h-5 w-5 text-green-600" />
            <p className="text-sm text-green-800">{successMessage}</p>
          </div>
        </div>
      )}

      {/* Summary Cards */}
      <div className="grid gap-4 sm:grid-cols-4">
        <button
          onClick={() => setDateFilter('all')}
          className={`rounded-lg border p-4 text-left transition-colors ${
            dateFilter === 'all' ? 'border-blue-500 bg-blue-50' : 'border-gray-200 bg-white hover:bg-gray-50'
          }`}
        >
          <p className="text-sm font-medium text-gray-600">All Executions</p>
          <p className="text-2xl font-bold text-gray-900">{overdueCount + todayCount + upcomingCount}</p>
        </button>
        <button
          onClick={() => setDateFilter('overdue')}
          className={`rounded-lg border p-4 text-left transition-colors ${
            dateFilter === 'overdue' ? 'border-red-500 bg-red-50' : 'border-gray-200 bg-white hover:bg-gray-50'
          }`}
        >
          <p className="text-sm font-medium text-red-600">Overdue</p>
          <p className="text-2xl font-bold text-red-700">{overdueCount}</p>
          <p className="text-xs text-gray-500">Past scheduled dates</p>
        </button>
        <button
          onClick={() => setDateFilter('today')}
          className={`rounded-lg border p-4 text-left transition-colors ${
            dateFilter === 'today' ? 'border-green-500 bg-green-50' : 'border-gray-200 bg-white hover:bg-gray-50'
          }`}
        >
          <p className="text-sm font-medium text-green-600">Today</p>
          <p className="text-2xl font-bold text-green-700">{todayCount}</p>
          <p className="text-xs text-gray-500">Scheduled for today</p>
        </button>
        <button
          onClick={() => setDateFilter('upcoming')}
          className={`rounded-lg border p-4 text-left transition-colors ${
            dateFilter === 'upcoming' ? 'border-blue-500 bg-blue-50' : 'border-gray-200 bg-white hover:bg-gray-50'
          }`}
        >
          <p className="text-sm font-medium text-blue-600">Upcoming</p>
          <p className="text-2xl font-bold text-blue-700">{upcomingCount}</p>
          <p className="text-xs text-gray-500">Future scheduled</p>
        </button>
      </div>

      {/* Filters */}
      <div className="rounded-lg border border-gray-200 bg-white p-4">
        <div className="flex flex-col gap-4 sm:flex-row">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
            >
              <option value="">All Statuses</option>
              <option value="SCHEDULED">Scheduled</option>
              <option value="STARTED">Started</option>
              <option value="IN_PROGRESS">In Progress</option>
              <option value="COMPLETED">Completed</option>
              <option value="CANCELLED">Cancelled</option>
              <option value="FAILED">Failed</option>
            </select>
          </div>
          <div className="flex items-end">
            <button
              onClick={() => {
                setStatusFilter('');
                setDateFilter('all');
              }}
              className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              Reset Filters
            </button>
          </div>
        </div>
      </div>

      {/* Executions Grid */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
        </div>
      ) : executions.length === 0 ? (
        <div className="rounded-lg border border-gray-200 bg-white p-8 text-center">
          <Clock className="mx-auto h-12 w-12 text-gray-400 mb-4" />
          <p className="text-gray-600 mb-2">No executions found</p>
          <p className="text-sm text-gray-500">
            Executions will appear here when routes are scheduled and executed by drivers.
          </p>
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {executions.map((execution) => (
            <ExecutionCard key={execution.id} execution={execution} />
          ))}
        </div>
      )}

      {/* Information Card */}
      <div className="rounded-lg border border-blue-200 bg-blue-50 p-6">
        <h3 className="font-semibold text-blue-900 mb-2">How Executions Work</h3>
        <ul className="space-y-1 text-sm text-blue-800 list-disc list-inside">
          <li>Executions are automatically created daily at 4:00 AM for active routes</li>
          <li>Drivers start executions to begin the collection workflow</li>
          <li>For each stop, drivers mark arrival and collect material (quantity + photo)</li>
          <li>Drivers can skip stops if material is not available</li>
          <li>Upon delivery completion, raw material transactions are auto-created in manufacturing-service</li>
          <li>Track real-time progress including distance traveled and quantity collected</li>
        </ul>
      </div>
    </div>
  );
}
